import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';

// ============== INTERFACES ==============

export interface CompanyProfile {
  id?: string;
  name: string;
  dri?: string;
  logo?: string;
  about?: string;
  stateOfIncorporation?: string;
  yearOfIncorporation?: number;
  feinNumber?: string;
  orgType?: string;
  diversityStatus?: string[];
  employees?: string;
  revenue?: string;
  locationsServiced?: string[];
  address?: CompanyAddress;
  websiteUrl?: string;
  phone?: string;
  email?: string;
  socialLinks?: SocialLinks;
  isPublic?: boolean;
}

export interface CompanyAddress {
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
}

export interface SocialLinks {
  linkedIn?: string;
  twitter?: string;
  youtube?: string;
  instagram?: string;
  tikTok?: string;
  facebook?: string;
}

export interface CompanyServiceItem {
  id?: string;
  name: string;
  category: 'IT_SERVICE' | 'INDUSTRY' | 'TECHNOLOGY';
  selected?: boolean;
  businessId?: string;
}

export interface PortfolioDocument {
  documentId: string;
  documentType: string; // CASE_STUDY | SALES_DECK | PROPOSAL | CERTIFICATION | TESTIMONIAL | OTHER
  fileId: string;
  fileUrl: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  isPublic: boolean;
  uploadedBy: string;
  uploadedOn: Date;
  displayOrder: number;
  status: string; // ACTIVE | ARCHIVED
}

export interface CompanyContact {
  id?: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  contactType: string;
  roleType?: string;
  isPublic?: boolean;
}

export interface OnboardUserRequest {
  firstName: string;
  lastName: string;
  email: string;
  userType: string;
}

export interface ContractRoleUser {
  userId: string;
  gigsumoNo: string;
  firstName: string;
  lastName: string;
  userName: string;
  emailId: string;
  appAccessStatus: string;
  roleType: string; // CONTRACT_MANAGER | BILLING_MANAGER | SIGNATURE_AUTHORITY
  createdOn: string;
}

export interface ContractRoleUsersRequest {
  searchContent: string | null;
  appAccessStatus: string | null;
  limit: number;
  searchAfterKey: any[] | null;
}

export interface PortfolioPublicSearchRequest {
  businessId: string;
  documentCategory?: string;
  searchContent?: string;
}

export interface BankAccount {
  id?: string;
  bankName: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  nameOnAccount?: string;
  accountType?: string;
  routingNumber?: string;
  accountNumber?: string;
  status: 'Active' | 'InActive';
}

export interface AuditLogEntry {
  id?: string;
  event: string;
  networks?: string;
  by: string;
  date: Date;
  time: string;
}

// Contact Types
export const CONTACT_TYPES = [
  'CEO',
  'Human Resources',
  'Contracts',
  'Accounts Receivables',
  'Accounts Payables'
];

// Document Types
export const DOCUMENT_TYPES = [
  { value: 'CASE_STUDY', label: 'Case Study' },
  { value: 'SALES_DECK', label: 'Sales Deck' },
  { value: 'PROPOSAL', label: 'Proposal' },
  { value: 'CERTIFICATION', label: 'Certification' },
  { value: 'TESTIMONIAL', label: 'Testimonial' },
  { value: 'OTHER', label: 'Other' }
];

// Contract Role Types
export const CONTRACT_ROLES = [
  { value: 'CONTRACT_MANAGER', label: 'Contract Manager' },
  { value: 'BILLING_MANAGER', label: 'Billing Manager' },
  { value: 'SIGNATURE_AUTHORITY', label: 'Signature Authority' }
];

// IT Services Options
export const IT_SERVICES = [
  'IT Strategy and Planning',
  'Digital Transformation Consulting',
  'Cybersecurity Consulting',
  'Custom Software Development',
  'Data Analytics and Business Intelligence',
  'Artificial Intelligence & Machine Learning',
  'IT Infrastructure Optimization',
  'Project Management and Implementation',
  'IT Compliance and Governance',
  'IT Training and Education',
  'Managed IT Services',
  'Permanent Staffing',
  'Temporary Staffing',
  'SOW Project Services',
  'BOR',
  'AOR',
  'Payroll Services'
];

// Industries Options
export const INDUSTRIES = [
  'Technology: Software, IT Services, Cybersecurity, AI, E-commerce.',
  'Finance & Insurance: Banking, Investment, Fintech.',
  'Healthcare & Social Assistance: Hospitals, Pharmaceuticals, Biotech.',
  'Manufacturing: Aerospace, Chemicals, Food, Electronics.',
  'Retail & Wholesale Trade: E-commerce, General Merchandise, Food Stores.',
  'Energy: Oil & Gas, Renewable Energy (Solar, Wind).',
  'Construction: Residential, Commercial, Heavy Construction.',
  'Hospitality & Recreation: Hotels, Restaurants, Tourism, Arts.',
  'Agriculture, Forestry, Fishing & Hunting: Farming, Logging, Aquaculture.',
  'Transportation & Warehousing: Trucking, Rail, Airlines, Logistics.',
  'Information & Media: Publishing, Broadcasting, Internet Services.',
  'Professional, Scientific & Technical Services: Engineering, Legal, Marketing, Research.',
  'Education: Public & Private Schools, Higher Education, EdTech.',
  'Public Administration & Services: Government, Legal, Protective Services.'
];

// Technologies Options
export const TECHNOLOGIES = [
  'Artificial Intelligence (AI) and Machine Learning (ML)',
  'Blockchain Technology',
  'Cloud Computing',
  'DevOps and AIOps',
  'Low-Code and No-Code Platforms',
  'Internet of Things (IoT)',
  'Augmented Reality (AR) and Virtual Reality',
  'Progressive Web Applications (PWAs)',
  'Microservices Architecture',
  'Edge Computing',
  'Quantum Computing (Emerging)',
  'Robotics and Automation',
  'Cybersecurity Technologies',
  '5G Technology',
  'Digital Twin Technology',
  'Hyperautomation',
  'Ethical AI and Explainable AI (XAI)'
];

// Diversity Status Options
export const DIVERSITY_OPTIONS = [
  'Minority Business Enterprise (MBE)',
  'Woman-Owned Business',
  'Veteran-Owned Business',
  'LGBTQ+-Owned Business',
  'Disability-Owned Business',
  'Small Disadvantaged Business (SDB)',
  'HUBZone Certified'
];

@Injectable({
  providedIn: 'root'
})
export class CompanyDataService {

  private companyProfileSubject = new BehaviorSubject<CompanyProfile | null>(null);
  companyProfile$ = this.companyProfileSubject.asObservable();

  constructor(private api: ApiService) {}

  // ============== PROFILE APIs ==============

  getCompanyProfile(businessId: string): Observable<any> {
    return this.api.query(`company/profile/${businessId}`);
  }

  getBusinessDetails(businessId: string): Observable<any> {
    return this.api.query(`business/details/${businessId}`);
  }

  updateCompanyProfile(businessId: string, data: Partial<CompanyProfile>): Observable<any> {
    return this.api.create(`company/profile/${businessId}`, data);
  }

  uploadCompanyLogo(businessId: string, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('logo', file);
    return this.api.create(`company/profile/${businessId}/logo`, formData);
  }

  // ============== SERVICES APIs ==============

  getCompanyServices(businessId: string): Observable<any> {
    return this.api.query(`business/services/${businessId}`);
  }

  updateCompanyServices(services: CompanyServiceItem[]): Observable<any> {
    return this.api.updatePut(`business/services/update`, services );
  }

  getAllServices(data:any): Observable<any> {
    return this.api.create('listvalue/findbyList',data);
  }

  // ============== PORTFOLIO APIs ==============

  getPortfolioDocuments(businessId: string): Observable<any> {
    return this.api.query(`api/contract/portfolio/${businessId}`);
  }

  uploadPortfolioDocument(businessId: string, formData: FormData): Observable<any> {
    return this.api.create(`api/contract/portfolio/upload/${businessId}`, formData);
  }

  updatePortfolioDocument(businessId: string, documentId: string, formData: FormData): Observable<any> {
    return this.api.updatePut(`api/contract/portfolio/update/${businessId}/${documentId}`, formData);
  }

  deletePortfolioDocument(businessId: string, documentId: string): Observable<any> {
    return this.api.delete(`api/contract/portfolio/remove/${businessId}/${documentId}`);
  }

  getPublicPortfolioDocuments(request: PortfolioPublicSearchRequest): Observable<any> {
    return this.api.create(`api/contract/portfolio/public`, request);
  }

  downloadPortfolioDocument(businessId: string, documentId: string): Observable<any> {
    return this.api.queryBlob(`api/contract/portfolio/download/${businessId}/${documentId}`);
  }

  // ============== CONTRACT ROLE USERS API ==============

  getContractRoleUsers(businessId: string, requestBody: ContractRoleUsersRequest): Observable<any> {
    return this.api.create(`api/contract/portfolio/contract-role-users/${businessId}`, requestBody);
  }

  // ============== PUBLIC APIs ==============

  getPublicBusinessDetails(businessId: string): Observable<any> {
    return this.api.query(`business/details/public/${businessId}`);
  }

  getPublicBusinessServices(businessId: string): Observable<any> {
    return this.api.query(`business/services/public/${businessId}`);
  }

  getPublicContractRoleUsers(businessId: string, requestBody: ContractRoleUsersRequest): Observable<any> {
    return this.api.create(`portfolio/contract-role-users/public/${businessId}`, requestBody);
  }

  // ============== VISIBILITY TOGGLE API ==============

  updateMenuVisibility(businessId: string, domain: string, visibility: boolean): Observable<any> {
    return this.api.updatePut(`business/update-visibility/${businessId}`, {
      domain,
      visibility
    });
  }

  // ============== CONTACTS APIs ==============

  getCompanyContacts(businessId: string): Observable<any> {
    return this.api.query(`company/contacts/${businessId}`);
  }

  addCompanyContact(businessId: string, contact: CompanyContact): Observable<any> {
    return this.api.create(`company/contacts/${businessId}`, contact);
  }

  updateCompanyContact(businessId: string, contactId: string, contact: Partial<CompanyContact>): Observable<any> {
    return this.api.create(`company/contacts/${businessId}/${contactId}`, contact);
  }

  deleteCompanyContact(businessId: string, contactId: string): Observable<any> {
    return this.api.delete(`company/contacts/${businessId}/${contactId}`);
  }

  onboardUsers(adminUserId: string, users: OnboardUserRequest[]): Observable<any> {
    return this.api.create(`user/onboardUsers?adminuserId=${adminUserId}`, users);
  }

  // ============== FINANCIALS APIs ==============

  getBankAccounts(businessId: string): Observable<any> {
    return this.api.query(`company/financials/bank-accounts/${businessId}`);
  }

  addBankAccount(businessId: string, account: BankAccount): Observable<any> {
    return this.api.create(`company/financials/bank-accounts/${businessId}`, account);
  }

  updateBankAccount(businessId: string, accountId: string, account: Partial<BankAccount>): Observable<any> {
    return this.api.create(`company/financials/bank-accounts/${businessId}/${accountId}`, account);
  }

  toggleBankAccountStatus(businessId: string, accountId: string, status: 'Active' | 'InActive'): Observable<any> {
    return this.api.create(`company/financials/bank-accounts/${businessId}/${accountId}/status`, { status });
  }

  deleteBankAccount(businessId: string, accountId: string): Observable<any> {
    return this.api.delete(`company/financials/bank-accounts/${businessId}/${accountId}`);
  }

  getAuditLog(businessId: string, page: number = 0, limit: number = 20): Observable<any> {
    return this.api.query(`company/financials/audit-log/${businessId}?page=${page}&limit=${limit}`);
  }
}
